import { QueryClient, QueryClientConfig } from '@tanstack/react-query';
import { NonUndefined } from 'utility-types';

import {
  createReactQueryContext,
  extractStatus,
  normalizeError,
} from './error-utils';
import { TReporting } from './types';

const UUID_SEGMENT_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type TReportingMeta = Record<string, unknown> & {
  reportingKey?: readonly unknown[];
};

function normalizeReactQueryErrorMessage(error: unknown): string {
  const normalizedError = normalizeError(error);
  const status = extractStatus(error);
  const message = normalizedError.message.trim();
  const lowerCaseMessage = message.toLowerCase();

  if (
    lowerCaseMessage === 'failed to fetch' ||
    lowerCaseMessage === 'network error' ||
    lowerCaseMessage === 'network request failed'
  ) {
    return 'Network Error';
  }

  if (lowerCaseMessage.includes('request aborted')) {
    return 'Request Aborted';
  }

  if (
    lowerCaseMessage.includes('timeout exceeded') ||
    lowerCaseMessage === 'timeout'
  ) {
    return 'Timeout';
  }

  if (status && status >= 500) {
    return `HTTP ${status}`;
  }

  return message || normalizedError.name || 'Unknown Error';
}

function normalizeReactQueryKeySegment(segment: unknown): string[] {
  if (segment === null || segment === undefined || segment === false) {
    return [];
  }

  if (Array.isArray(segment)) {
    return segment.flatMap(normalizeReactQueryKeySegment);
  }

  if (typeof segment === 'string') {
    return UUID_SEGMENT_PATTERN.test(segment) ? [] : [segment];
  }

  if (typeof segment === 'number') {
    return [String(segment)];
  }

  if (segment === true) {
    return ['true'];
  }

  return [];
}

function createReactQueryLabel(key?: readonly unknown[]): string {
  if (!key?.length) {
    return 'unknown';
  }

  const normalizedSegments = key.flatMap(normalizeReactQueryKeySegment);
  return normalizedSegments.length > 0
    ? normalizedSegments.join(',')
    : 'unknown';
}

function getReportingKey(
  meta?: TReportingMeta
): readonly unknown[] | undefined {
  return Array.isArray(meta?.reportingKey) ? meta.reportingKey : undefined;
}

/**
 * Default configuration for React Query client.
 * Uses conservative defaults suitable for production use.
 */
export const DEFAULT_QUERY_CLIENT_OPTIONS: NonUndefined<
  QueryClientConfig['defaultOptions']
> = {
  queries: {
    /**
     * Data is considered fresh immediately after fetching.
     * Set to 0 for most up-to-date data; increase for less frequent refetches.
     */
    staleTime: 0,

    /**
     * Garbage collection time: 5 minutes.
     * Inactive query data will be removed from cache after this duration.
     */
    gcTime: 5 * 60 * 1000,

    /**
     * Retry failed queries up to 3 times with exponential backoff.
     */
    retry: 3,

    /**
     * Don't refetch on window focus by default.
     * Enable per-query if real-time data is needed.
     */
    refetchOnWindowFocus: false,
  },
  mutations: {
    /**
     * Don't retry mutations by default to avoid duplicate side effects.
     */
    retry: false,
  },
};

export interface ICreateQueryClientOptions {
  /**
   * Custom configuration to merge with defaults.
   */
  config?: Partial<QueryClientConfig>;

  /**
   * Reporting service for error logging.
   * If provided, query and mutation errors will be logged.
   */
  reporting?: TReporting;
}

/**
 * Determines whether an error should be reported based on HTTP status and meta config.
 *
 * - `meta.reportError: true` → always report
 * - `meta.reportError: false` → never report
 * - Otherwise: report 5xx and non-HTTP errors (network failures), skip 4xx
 */
export function shouldReportError(
  error: unknown,
  meta?: Record<string, unknown>
): boolean {
  const reportError = meta?.reportError;
  if (reportError === true) return true;
  if (reportError === false) return false;

  const status = extractStatus(error);
  // status 0 = network failure (CORS, DNS, etc), treat as reportable
  return status === undefined || status === 0 || status >= 500;
}

/**
 * Creates a configured QueryClient instance with sensible defaults
 * and optional error logging integration.
 *
 * @example
 * ```ts
 * const queryClient = createQueryClient({
 *   reporting: useReporting(),
 * });
 * ```
 */
export function createQueryClient(
  options: ICreateQueryClientOptions = {}
): QueryClient {
  const { config, reporting } = options;

  const queryClient = new QueryClient({
    defaultOptions: {
      ...DEFAULT_QUERY_CLIENT_OPTIONS,
      ...config?.defaultOptions,
      queries: {
        ...DEFAULT_QUERY_CLIENT_OPTIONS.queries,
        ...config?.defaultOptions?.queries,
      },
      mutations: {
        ...DEFAULT_QUERY_CLIENT_OPTIONS.mutations,
        ...config?.defaultOptions?.mutations,
      },
    },
  });

  // Set up global error handlers if reporting is provided
  if (reporting) {
    const queryCache = queryClient.getQueryCache();
    const mutationCache = queryClient.getMutationCache();

    queryCache.subscribe((event) => {
      if (event.type === 'updated' && event.query.state.status === 'error') {
        const error = event.query.state.error;
        const meta = event.query.options.meta as TReportingMeta | undefined;

        if (!shouldReportError(error, meta)) return;

        const reportingKey = getReportingKey(meta) ?? event.query.queryKey;
        const context = createReactQueryContext({
          queryKey: reportingKey,
          error,
        });

        reporting.error(
          `React Query: Query error – ${normalizeReactQueryErrorMessage(
            error
          )} – ${createReactQueryLabel(reportingKey)}`,
          context
        );
      }
    });

    mutationCache.subscribe((event) => {
      if (event.type === 'updated' && event.mutation.state.status === 'error') {
        const error = event.mutation.state.error;
        const meta = event.mutation.options.meta as TReportingMeta | undefined;

        if (!shouldReportError(error, meta)) return;

        const mutationKey =
          getReportingKey(meta) ?? event.mutation.options.mutationKey;
        const context = createReactQueryContext({
          mutationKey,
          error,
        });

        reporting.error(
          `React Query: Mutation Error – ${normalizeReactQueryErrorMessage(
            error
          )} – ${createReactQueryLabel(mutationKey)}`,
          context
        );
      }
    });
  }

  return queryClient;
}
